import { z } from 'zod'

import { TemplateFieldSchema } from 'shared/api/common/schemas/templates/templateField'
import { UserFieldSchema } from 'shared/api/common/schemas/userField'

import { BlankPageErrorSchema, InvalidLinkErrorSchema } from './errors'

const ObjectOrArraySchema = z.union([
  z.record(z.string(), z.array(z.string())),
  z.array(z.unknown()),
])

// Base schemas for reusable components
const ChannelFlagsSchema = z.object({
  has_fb_channel: z.boolean(),
  has_wa_channel: z.boolean(),
  has_ig_channel: z.boolean(),
  has_tg_channel: z.boolean(),
  has_tt_channel: z.boolean(),
  has_email_channel: z.boolean(),
  has_sms_channel: z.boolean(),
  has_webchat_channel: z.boolean(),
})

const BaseModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  template_installation_id: z.unknown().nullable(),
  is_corrupted: z.boolean(),
  is_pro: z.boolean(),
})

const TemplateIdSchema = z.object({
  id: z.string(),
  name: z.string(),
  template_installation_id: z.unknown().nullable(),
})

const GrowthToolSchema = BaseModelSchema.merge(ChannelFlagsSchema)

const KeywordSchema = BaseModelSchema.merge(ChannelFlagsSchema).extend({
  keywords: z.array(z.string()),
  keyword_rules: z.array(
    z.object({
      keywords: z.array(z.string()),
      condition: z.string(),
    }),
  ),
  is_system: z.boolean(),
})

const FlowSchema = BaseModelSchema.merge(ChannelFlagsSchema).extend({
  is_ai_addon: z.boolean(),
  is_draft: z.boolean(),
  has_message_tags: z.boolean(),
  has_one_time_notify_messages: z.boolean(),
  folder_path: z.string(),
  is_locked_flow: z.boolean(),
})

export const TemplateInstallationMapSchema = z.object({
  models: z.object({
    sequences: z.array(z.unknown()),
    growth_tools: z.array(GrowthToolSchema),
    keywords: z.array(KeywordSchema),
    intents: z.array(z.unknown()),
    flows: z.array(FlowSchema),
    generals: z.array(BaseModelSchema),
    integrations: z.array(z.unknown()),
    triggers: z.array(BaseModelSchema),
    external_triggers: z.array(z.unknown()),
    template_installations: z.array(z.unknown()),
  }),
  entities: z.object({
    tags: z.array(TemplateIdSchema),
    zapier_events: z.array(z.unknown()),
    custom_fields: z.array(TemplateIdSchema),
    global_fields: z.array(TemplateIdSchema),
    custom_events: z.array(z.unknown()),
    otn_reasons: z.array(
      TemplateIdSchema.extend({
        request_title: z.string().nullable(),
      }),
    ),
    apps: z.array(z.unknown()),
  }),
  dependencies: z.object({
    sequence_messages: ObjectOrArraySchema,
    growth_tools: ObjectOrArraySchema,
    keywords: ObjectOrArraySchema,
    intents: ObjectOrArraySchema,
    flows: ObjectOrArraySchema,
    triggers: ObjectOrArraySchema,
    external_triggers: ObjectOrArraySchema,
    generals: ObjectOrArraySchema,
  }),
  global_fields: z.array(
    z.object({
      field_id: z.number(),
      type: z.string(),
      segment_type: z.string(),
      caption: z.string(),
      description: z.string(),
      value: z.unknown().nullable(),
      status: z.string(),
      is_template_field: z.boolean(),
      template_installation_id: z.unknown().nullable(),
      folder_id: z.number(),
    }),
  ),
})

const TemplateInstallationSchema = z.object({
  template_data: z.object({
    template_id: z.number(),
    title: z.string(),
    is_protected: z.boolean(),
    is_editable: z.boolean(),
    is_tracked: z.boolean(),
    flows_locked_status: z.record(z.string(), z.boolean()),
    description: z
      .object({
        summary: z.string(),
        guideLink: z.string().nullable(),
        videoGuideLink: z.string().nullable(),
      })
      .nullable(),
    is_shared: z.boolean(),
    share_hash: z.string().nullable(),
    share_link: z.string().nullable(),
    share_type: z.number(),
    current_version: z.object({
      page_id: z.string(),
      is_pro: z.boolean(),
      base_page_name: z.string(),
      template_fields: z.array(TemplateFieldSchema),
      structure: z.array(z.string()),
      map: TemplateInstallationMapSchema,

      custom_fields: z.array(UserFieldSchema),
      folders: z.array(
        z.object({
          type: z.string(),
          folder_id: z.number(),
          path: z.string(),
          title: z.string(),
          entity: z.number(),
          created: z.number(),
          modified: z.number(),
        }),
      ),

      is_ai_addon: z.boolean(),
    }),
    image: z
      .object({
        avatar_url_100x100: z.string().nullable(),
        color: z.string(),
      })
      .optional(),
    template_access_data: z
      .object({
        is_protected: z.boolean(),
        is_locked: z.boolean(),
        is_tracked: z.boolean(),
        is_owner: z.boolean(),
      })
      .optional(),
  }),
  user_info: z.object({
    name: z.string().nullable(),
    avatar: z.string().nullable(),
  }),
})

const FieldMappingSchema = z.record(
  z.number().or(z.string().regex(/\d+/)),
  z.number().or(z.string().regex(/\d+/)).nullable(),
)

export const TemplateInstallRequestSchema = z.object({
  page_id: z.string(),
  replace_welcome_message: z.boolean(),
  replace_default_reply: z.boolean(),
  replace_main_menu: z.boolean(),
  replace_greeting_text: z.boolean(),
  replace_ig_default_reply: z.boolean(),
  replace_story_mention_reply: z.boolean(),
  replace_ice_breakers: z.boolean(),
  replace_ig_ice_breakers: z.boolean(),
  replace_ig_main_menu: z.boolean(),
  mapping_settings: z.object({
    custom_fields: FieldMappingSchema,
    tags: FieldMappingSchema,
    global_fields: FieldMappingSchema,
    otn_reasons: FieldMappingSchema,
    custom_events: FieldMappingSchema,
  }),
  fill_fields: z.looseObject({}).optional(),
  async: z.string(),
})

export const TemplateApiSchemas = {
  getData: {
    path: z.object({
      template_hash: z.string(),
    }),
    query: z.undefined(),
    response: TemplateInstallationSchema,
    errors: {
      InvalidLinkError: InvalidLinkErrorSchema,
    },
  },
  install: {
    path: z.object({
      template_hash: z.string(),
    }),
    query: z.undefined(),
    request: TemplateInstallRequestSchema,
    response: z.object({
      async: z.string(),
    }),
    errors: {
      InvalidLinkError: InvalidLinkErrorSchema,
      BlankPageError: BlankPageErrorSchema,
    },
  },
}

export type TemplateInstallation = z.infer<typeof TemplateInstallationSchema>
export type TemplateInstallRequest = z.infer<typeof TemplateInstallRequestSchema>
